import { useMemo } from 'react';
import { t, useLanguage, type Language } from '@/app/utils/i18n';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import type { TooltipProps } from 'recharts';
import type { AqlGrowthDashboardPoint } from '@/app/services/growth-service';

interface GrowthTrendChartProps {
  series: AqlGrowthDashboardPoint[];
  loading?: boolean;
}

interface ChartRow {
  date: string;
  label: string;
  median: number | null;
  avg: number | null;
  p25: number | null;
  p75: number | null;
  p10: number | null;
  p90: number | null;
  /** Area data — only the gap between p10 and p25 (lower whisker). */
  p10Range: [number, number] | null;
  /** Area data — only the gap between p75 and p90 (upper whisker). */
  p90Range: [number, number] | null;
  /** Core band p25–p75 as a [min, max] tuple for Recharts area. */
  coreRange: [number, number] | null;
  sample: number | null;
  confidence: number | null;
  uniformity: number | null;
  warning: 'normal' | 'warning' | 'critical' | null;
}

const DATE_LOCALES: Record<Language, string> = {
  pt: 'pt-PT',
  en: 'en-GB',
  fr: 'fr-FR',
  es: 'es-ES',
  de: 'de-DE'
};

const buildRows = (series: AqlGrowthDashboardPoint[], language: Language): ChartRow[] =>
  series.map((point) => {
    const median = point.median_size;
    const avg = point.avg_size;
    const p25 = point.p25_size;
    const p75 = point.p75_size;
    const p10 = point.p10_size;
    const p90 = point.p90_size;

    const coreRange: [number, number] | null = p25 !== null && p75 !== null ? [p25, p75] : null;
    const p10Range: [number, number] | null = p10 !== null && p25 !== null ? [p10, p25] : null;
    const p90Range: [number, number] | null = p75 !== null && p90 !== null ? [p75, p90] : null;

    const dateObj = new Date(point.period_start);
    const label = Number.isNaN(dateObj.getTime())
      ? point.period_start
      : dateObj.toLocaleDateString(DATE_LOCALES[language], { month: 'short', day: '2-digit' });

    return {
      date: point.period_start,
      label,
      median,
      avg,
      p25,
      p75,
      p10,
      p90,
      coreRange,
      p10Range,
      p90Range,
      sample: point.sample_count,
      confidence: point.confidence,
      uniformity: point.uniformity_score,
      warning: point.warning_level
    };
  });

const formatPct = (value: number | null | undefined) =>
  value === null || value === undefined ? '—' : `${Math.round(value * 100)}%`;

const formatMm = (value: number | null | undefined) =>
  value === null || value === undefined ? '—' : `${value.toFixed(1)} mm`;

function CustomTooltip({ active, payload, language }: TooltipProps<number, string> & { language: Language }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload as ChartRow | undefined;
  if (!row) return null;
  const tr = (key: string) => t('vision', key, language);

  const warningColor = row.warning === 'critical'
    ? 'text-red-600 dark:text-red-300'
    : row.warning === 'warning'
      ? 'text-amber-600 dark:text-amber-300'
      : 'text-emerald-600 dark:text-emerald-300';

  return (
    <div className="rounded-lg border border-border bg-popover/95 p-3 text-xs text-popover-foreground shadow-lg backdrop-blur">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{row.label}</p>
      <dl className="mt-2 space-y-1">
        <div className="flex justify-between gap-4">
          <dt className="text-muted-foreground">{tr('medianSize')}</dt>
          <dd className="font-mono font-semibold">{formatMm(row.median)}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-muted-foreground">{tr('averageSize')}</dt>
          <dd className="font-mono">{formatMm(row.avg)}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-muted-foreground">{tr('validSample')}</dt>
          <dd className="font-mono">{row.sample ?? '—'}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-muted-foreground">{tr('confidence')}</dt>
          <dd className="font-mono">{formatPct(row.confidence)}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-muted-foreground">{tr('cohortUniformity')}</dt>
          <dd className="font-mono">{formatPct(row.uniformity)}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-muted-foreground">{tr('warning')}</dt>
          <dd className={`font-semibold uppercase ${warningColor}`}>
            {row.warning === 'critical' ? tr('warningCritical') : row.warning === 'warning' ? tr('warningWarning') : tr('warningNormal')}
          </dd>
        </div>
      </dl>
    </div>
  );
}

export function GrowthTrendChart({ series, loading }: GrowthTrendChartProps) {
  const language = useLanguage();
  const tr = (key: string) => t('vision', key, language);
  const data = useMemo(() => buildRows(series, language), [language, series]);
  const hasWideBand = data.some((row) => row.p10Range && row.p90Range);

  if (loading) {
    return (
      <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
        <div className="h-5 w-44 animate-pulse rounded bg-muted/70" />
        <div className="mt-4 h-72 animate-pulse rounded bg-muted/40" />
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
        <h3 className="text-base font-semibold text-foreground">{tr('growthTrend')}</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          {tr('noGrowthMetricsAvailable')}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold text-foreground">{tr('growthTrend')}</h3>
          <p className="text-xs text-muted-foreground">
            {tr('growthTrendSubtitle')}
          </p>
        </div>
      </div>

      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 10, right: 16, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id="growthCoreBand" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#22d3ee" stopOpacity={0.32} />
                <stop offset="100%" stopColor="#0ea5e9" stopOpacity={0.18} />
              </linearGradient>
              <linearGradient id="growthWideBand" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#0ea5e9" stopOpacity={0.14} />
                <stop offset="100%" stopColor="#0ea5e9" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="currentColor" strokeOpacity={0.08} vertical={false} />
            <XAxis
              dataKey="label"
              stroke="currentColor"
              strokeOpacity={0.45}
              fontSize={11}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              stroke="currentColor"
              strokeOpacity={0.45}
              fontSize={11}
              tickLine={false}
              axisLine={false}
              width={42}
              tickFormatter={(value) => `${value} mm`}
            />
            <Tooltip content={<CustomTooltip language={language} />} cursor={{ stroke: 'currentColor', strokeOpacity: 0.18 }} />
            <Legend
              wrapperStyle={{ fontSize: 11, paddingTop: 6 }}
              iconSize={10}
              iconType="circle"
            />

            {hasWideBand ? (
              <>
                <Area
                  type="monotone"
                  dataKey="p10Range"
                  name="p10 → p25"
                  stroke="none"
                  fill="url(#growthWideBand)"
                  isAnimationActive={false}
                  legendType="none"
                />
                <Area
                  type="monotone"
                  dataKey="p90Range"
                  name="p75 → p90"
                  stroke="none"
                  fill="url(#growthWideBand)"
                  isAnimationActive={false}
                  legendType="none"
                />
              </>
            ) : null}

            <Area
              type="monotone"
              dataKey="coreRange"
              name={tr('coreDistribution')}
              stroke="#22d3ee"
              strokeOpacity={0.35}
              fill="url(#growthCoreBand)"
              isAnimationActive={false}
            />

            <Line
              type="monotone"
              dataKey="avg"
              name={tr('averageSize')}
              stroke="#94a3b8"
              strokeWidth={1.4}
              strokeDasharray="4 4"
              dot={false}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="median"
              name={tr('medianSize')}
              stroke="#38bdf8"
              strokeWidth={2.4}
              dot={{ r: 3, strokeWidth: 0, fill: '#38bdf8' }}
              activeDot={{ r: 5, strokeWidth: 0, fill: '#7dd3fc' }}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
